/**
 * Fixes links on Webflow staging environments
 * Converts relative links and links pointing to the live domain (jamesbattye.dev)
 * to use the current staging domain
 */

const LIVE_DOMAIN = "jamesbattye.dev";

/**
 * Checks if the current URL is a Webflow staging environment
 */
export function isWebflowStaging(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.includes(".webflow.io");
}

/**
 * Checks if a URL is relative (starts with /) or points to the live domain
 */
function needsStagingFix(href: string): boolean {
  if (!href) return false;

  // Check if it's a relative link
  if (href.startsWith("/")) return true;

  // Check if it points to the live domain
  try {
    const url = new URL(href, window.location.origin);
    return url.hostname === LIVE_DOMAIN;
  } catch {
    // If URL parsing fails, check if it contains the live domain
    return href.includes(LIVE_DOMAIN);
  }
}

/**
 * Converts a link to be relative (extracts pathname from full URLs)
 */
function convertToRelative(href: string): string {
  if (!href) return href;

  // If it's already relative, return as is
  if (href.startsWith("/")) {
    return href;
  }

  // If it's a full URL, extract the pathname to make it relative
  try {
    const url = new URL(href, window.location.origin);
    // Return the pathname + search + hash to preserve query params and anchors
    return url.pathname + url.search + url.hash;
  } catch {
    // If URL parsing fails, try to extract path from string
    if (href.includes(LIVE_DOMAIN)) {
      const pathMatch = href.match(/https?:\/\/[^\/]+(\/.*)/);
      if (pathMatch && pathMatch[1]) {
        return pathMatch[1];
      }
    }
    // If we can't parse it, return as is
    return href;
  }
}

/**
 * Fixes all links on the page to be relative
 * Only runs if the current URL is a Webflow staging environment
 */
export function fixStagingLinks(): void {
  if (typeof window === "undefined") return;

  // Only run on Webflow staging environments
  if (!isWebflowStaging()) {
    return;
  }

  // Find all anchor tags with href attributes
  const links = document.querySelectorAll<HTMLAnchorElement>("a[href]");
  let fixedCount = 0;

  links.forEach((link) => {
    const originalHref = link.getAttribute("href");
    if (!originalHref) return;

    // Check if this link needs to be fixed
    if (needsStagingFix(originalHref)) {
      const newHref = convertToRelative(originalHref);
      link.setAttribute("href", newHref);
      fixedCount++;
    }
  });

}

